import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge, Banner, BlockStack, Box, Button, Card, EmptyState, InlineStack, Layout, Modal, Page,
  Spinner, Tabs, Text,
} from '@shopify/polaris';
import { apiFetch } from '../api';

// Moderación de la comunidad de la APP móvil.
//
// ── Cuál comunidad es esta ──
// La que usa la app vive en `mobile-api`. La página «Comunidad (web)»
// (`CommunityModeration.tsx`) modera otra, la del backend, que la app ya no
// usa. Esta llama a `/panel-api/comunidad-app/*`, el puente del backend que
// añade el token de servicio en el servidor (ver `community-app-admin.ts`).
//
// ── Qué hace una persona aquí ──
// La app oculta sola lo que acumula denuncias, y eso es abusable. Aquí se
// revisa: se RESTAURA (las denuncias ya juzgadas dejan de contar) o se RETIRA.
// Lo que borró su propia autora no se restaura nunca: es su derecho de
// supresión, y `mobile-api` lo rechaza igualmente.

type Kind = 'publicacion' | 'comentario';

type ReportedItem = {
  id: string;
  kind: Kind;
  post_id?: string;
  display_name: string;
  stage?: string;
  topic?: string | null;
  body: string;
  created_at: string;
  removed_at: string | null;
  removed_by: string | null;
  moderated_at: string | null;
  reports: number;
  pending: number;
  reasons: (string | null)[] | null;
  last_report_at: string | null;
};

type Action = 'restaurar' | 'retirar';

const REASONS: Record<string, string> = {
  spam: 'Spam',
  abuse: 'Abuso o acoso',
  misinformation: 'Desinformación',
  privacy: 'Privacidad',
  other: 'Otro motivo',
};

const STAGES: Record<string, string> = {
  cycle: 'Ciclo',
  conceive: 'Búsqueda de embarazo',
  pregnancy: 'Embarazo',
  postpartum: 'Posparto',
  menopause: 'Menopausia',
};

const TOPICS: Record<string, string> = {
  endometriosis: 'Endometriosis',
  sop: 'SOP',
  buscando: 'Buscando',
  perdida: 'Pérdida',
};

type Status = { label: string; tone?: 'critical' | 'warning' | 'success' | 'info' };

function statusOf(item: ReportedItem): Status {
  if (item.removed_by === 'autora') return { label: 'Borrada por su autora' };
  if (item.removed_by === 'moderacion') return { label: 'Retirada por moderación', tone: 'critical' };
  if (item.removed_at) return { label: 'Oculta por denuncias', tone: 'warning' };
  if (item.moderated_at) return { label: 'Restaurada', tone: 'success' };
  return { label: 'Visible', tone: 'info' };
}

const dateFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' });
function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : dateFormat.format(date);
}

function topicOf(item: ReportedItem): string {
  if (item.kind === 'comentario') return 'Comentario en una publicación';
  const parts = [item.stage ? STAGES[item.stage] ?? item.stage : null, item.topic ? TOPICS[item.topic] ?? item.topic : null];
  return parts.filter(Boolean).join(' · ') || 'Sin tema';
}

// `apiFetch` lanza «API Error 404: {json}». Se traduce el código de
// `mobile-api` a algo que la persona del panel pueda entender y actuar.
function explain(problem: unknown): string {
  const message = problem instanceof Error ? problem.message : String(problem);
  const match = message.match(/^API Error (\d+): ([\s\S]*)$/);
  if (!match) return `No se pudo conectar con el servidor. ${message}`;
  const status = Number(match[1]);
  let code = '';
  try { code = JSON.parse(match[2])?.error ?? ''; } catch { /* cuerpo no JSON */ }
  switch (code) {
    case 'mobile_admin_token_missing':
      return 'El servidor no tiene configurado MOBILE_ADMIN_TOKEN, así que no puede hablar con la API de la app.';
    case 'post_not_found':
    case 'comment_not_found':
      return 'Ese contenido ya no existe o lo borró su autora, así que no se puede moderar.';
    case 'invalid_request':
      return 'La petición no es válida (identificador o acción incorrectos).';
  }
  if (status === 401 || status === 403) return 'No autorizado: la sesión del panel o el token de servicio no son válidos.';
  return `Error ${status} al hablar con la API de la app.`;
}

const TABS = [
  { id: 'pendientes', content: 'Pendientes', accessibilityLabel: 'Denuncias pendientes de revisar', panelID: 'app-community-panel' },
  { id: 'revisadas', content: 'Revisadas', accessibilityLabel: 'Denuncias ya revisadas', panelID: 'app-community-panel' },
];

export default function AppCommunityModeration() {
  const [tab, setTab] = useState(0);
  const [items, setItems] = useState<ReportedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState('');
  const [confirm, setConfirm] = useState<{ item: ReportedItem; action: Action } | null>(null);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await apiFetch<{ posts: ReportedItem[]; comments: ReportedItem[] }>('/panel-api/comunidad-app/denuncias');
      setItems([
        ...(data.posts ?? []).map(post => ({ ...post, kind: 'publicacion' as const })),
        ...(data.comments ?? []).map(comment => ({ ...comment, kind: 'comentario' as const })),
      ]);
    } catch (problem) {
      setLoadError(explain(problem));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const pendingItems = useMemo(() => items.filter(item => item.pending > 0), [items]);
  const reviewedItems = useMemo(() => items.filter(item => item.pending <= 0), [items]);
  const visible = useMemo(() => {
    const list = tab === 0 ? pendingItems : reviewedItems;
    return [...list].sort((a, b) =>
      new Date(b.last_report_at ?? b.created_at).getTime() - new Date(a.last_report_at ?? a.created_at).getTime());
  }, [tab, pendingItems, reviewedItems]);

  const tabs = TABS.map((entry, index) => ({
    ...entry,
    content: `${entry.content} (${index === 0 ? pendingItems.length : reviewedItems.length})`,
  }));

  const openConfirm = (item: ReportedItem, action: Action) => {
    setActionError('');
    setConfirm({ item, action });
  };

  const act = async () => {
    if (!confirm) return;
    const { item, action } = confirm;
    setActing(true);
    setActionError('');
    try {
      const segment = item.kind === 'publicacion' ? 'publicaciones' : 'comentarios';
      await apiFetch(`/panel-api/comunidad-app/${segment}/${encodeURIComponent(item.id)}/moderar`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      });
      const isPost = item.kind === 'publicacion';
      const what = isPost ? 'La publicación' : 'El comentario';
      const result = action === 'restaurar' ? 'queda visible' : `queda ${isPost ? 'retirada' : 'retirado'} de la comunidad`;
      setNotice(`${what} de ${item.display_name} ${result}.`);
      setConfirm(null);
      await load();
    } catch (problem) {
      setActionError(explain(problem));
    } finally {
      setActing(false);
    }
  };

  const confirmTitle = confirm
    ? `${confirm.action === 'restaurar' ? (confirm.item.removed_at ? 'Restaurar' : 'Mantener visible') : 'Retirar'} ${confirm.item.kind === 'publicacion' ? 'publicación' : 'comentario'}`
    : '';

  return (
    <Page
      title="Comunidad de la app"
      subtitle="Denuncias de publicaciones y comentarios de la comunidad de la app móvil"
      secondaryActions={[{ content: 'Recargar', onAction: () => { void load(); }, disabled: loading }]}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <div aria-live="polite">
              {notice && (
                <Banner tone="success" onDismiss={() => setNotice('')}>
                  <p>{notice}</p>
                </Banner>
              )}
            </div>
            {loadError && (
              <Banner tone="critical" title="No se pudieron cargar las denuncias" action={{ content: 'Reintentar', onAction: () => { void load(); } }}>
                <p>{loadError}</p>
              </Banner>
            )}
            <Card padding="0">
              <Tabs tabs={tabs} selected={tab} onSelect={setTab}>
                <Box padding="400" id="app-community-panel">
                  {loading ? (
                    <InlineStack align="center" gap="200" blockAlign="center">
                      <Spinner size="small" accessibilityLabel="Cargando denuncias" />
                      <Text as="span" tone="subdued">Cargando denuncias…</Text>
                    </InlineStack>
                  ) : visible.length === 0 ? (
                    <EmptyState heading={tab === 0 ? 'No hay denuncias pendientes' : 'Todavía no hay denuncias revisadas'} image="">
                      <p>{tab === 0 ? 'Cuando alguien denuncie una publicación o un comentario, aparecerá aquí.' : 'Lo que restaures o retires aparecerá aquí.'}</p>
                    </EmptyState>
                  ) : (
                    <BlockStack as="ul" gap="400">
                      {visible.map(item => (
                        <ReportCard key={`${item.kind}-${item.id}`} item={item} onAction={openConfirm} />
                      ))}
                    </BlockStack>
                  )}
                </Box>
              </Tabs>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>

      <Modal
        open={confirm !== null}
        onClose={() => { if (!acting) setConfirm(null); }}
        title={confirmTitle}
        primaryAction={{
          content: confirm?.action === 'retirar' ? 'Retirar' : (confirm?.item.removed_at ? 'Restaurar' : 'Mantener visible'),
          destructive: confirm?.action === 'retirar',
          loading: acting,
          onAction: () => { void act(); },
        }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setConfirm(null), disabled: acting }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {actionError && (
              <Banner tone="critical" title="No se pudo completar la acción">
                <p>{actionError}</p>
              </Banner>
            )}
            {confirm && (
              <>
                <Text as="p">
                  {confirm.action === 'retirar'
                    ? 'Dejará de verse en la comunidad de la app. El texto se conserva para poder revisarlo y restaurarlo más adelante.'
                    : confirm.item.removed_at
                      ? 'Volverá a verse en la comunidad de la app. Las denuncias que ya tiene quedan juzgadas y no volverán a ocultarlo por sí solas.'
                      : 'Seguirá visible y las denuncias que ya tiene quedan juzgadas: no volverán a ocultarlo por sí solas.'}
                </Text>
                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                  <BlockStack gap="100">
                    <Text as="p" tone="subdued">{confirm.item.display_name}</Text>
                    <Text as="p">{confirm.item.body || 'Sin texto.'}</Text>
                  </BlockStack>
                </Box>
              </>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}

function ReportCard({ item, onAction }: { item: ReportedItem; onAction: (item: ReportedItem, action: Action) => void }) {
  const status = statusOf(item);
  const byAuthor = item.removed_by === 'autora';
  const retiredByModeration = item.removed_by === 'moderacion';
  const kindLabel = item.kind === 'publicacion' ? 'Publicación' : 'Comentario';
  const reasons = (item.reasons ?? []).filter((reason): reason is string => Boolean(reason)).map(reason => REASONS[reason] ?? reason);
  const restoreLabel = item.removed_at ? 'Restaurar' : 'Mantener visible';
  const headingId = `report-${item.kind}-${item.id}`;

  return (
    <Box as="li">
      <Card>
        <section aria-labelledby={headingId}>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="start" gap="200" wrap>
              <BlockStack gap="050">
                <Text as="h3" variant="headingSm" id={headingId}>
                  {kindLabel} de {item.display_name}
                </Text>
                <Text as="p" tone="subdued">
                  {topicOf(item)} · {item.kind === 'publicacion' ? 'publicada' : 'publicado'} el {formatDate(item.created_at)}
                </Text>
              </BlockStack>
              <InlineStack gap="200">
                {item.pending > 0 && <Badge tone="attention">{`Pendiente: ${item.pending}`}</Badge>}
                <Badge tone={status.tone}>{status.label}</Badge>
              </InlineStack>
            </InlineStack>

            <Text as="p" breakWord>
              {byAuthor ? <Text as="span" tone="subdued">(Texto borrado por su autora)</Text> : item.body || <Text as="span" tone="subdued">(Sin texto)</Text>}
            </Text>

            <BlockStack gap="100">
              <Text as="p">
                <Text as="span" fontWeight="semibold">Denuncias: </Text>
                {item.reports} en total
                {item.pending > 0 ? `, ${item.pending} sin revisar` : ''}
                {item.last_report_at ? ` · última el ${formatDate(item.last_report_at)}` : ''}
              </Text>
              <Text as="p">
                <Text as="span" fontWeight="semibold">Motivos: </Text>
                {reasons.length ? reasons.join(', ') : 'Sin motivo indicado'}
              </Text>
              {item.moderated_at && (
                <Text as="p" tone="subdued">Revisada por moderación el {formatDate(item.moderated_at)}</Text>
              )}
            </BlockStack>

            {byAuthor ? (
              <InlineStack gap="200" blockAlign="center">
                <Button disabled accessibilityLabel={`Restaurar no disponible: ${kindLabel.toLowerCase()} borrado por su autora`}>Restaurar</Button>
                <Text as="span" tone="subdued">
                  Lo borró su propia autora y no se puede restaurar: es su derecho de supresión.
                </Text>
              </InlineStack>
            ) : (
              <InlineStack gap="200" blockAlign="center">
                <Button
                  onClick={() => onAction(item, 'restaurar')}
                  accessibilityLabel={`${restoreLabel} ${kindLabel.toLowerCase()} de ${item.display_name}`}
                >
                  {restoreLabel}
                </Button>
                <Button
                  tone="critical"
                  disabled={retiredByModeration}
                  onClick={() => onAction(item, 'retirar')}
                  accessibilityLabel={`Retirar ${kindLabel.toLowerCase()} de ${item.display_name}`}
                >
                  Retirar
                </Button>
                {retiredByModeration && <Text as="span" tone="subdued">{item.kind === 'publicacion' ? 'Ya está retirada' : 'Ya está retirado'} por moderación.</Text>}
              </InlineStack>
            )}
          </BlockStack>
        </section>
      </Card>
    </Box>
  );
}
